import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { messageToClientFormat, shouldCopyMacro } from '../../../../util';
import { DraftHelpers } from 'util/Draft';
import { saveNewMessageEditorState } from 'actions';
import { addEdgeToDecisionTreePath } from 'actions/searchActions';
import {
  extractLastIncomingMessage,
  extractNonHistoricalTicketIdFromState,
  extractSelectedInboxTab,
  extractSelectedTicketFromState,
  getNewMessagesEditor,
} from 'extractFromState';
import useAgentAssist from './hooks/useAgentAssist';
import Loader from './components/Loader/Loader';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import { trackAgentAssistUsefulnessFailure } from './analytics';
import {
  Root,
  MainBody,
  Section,
  MacroHeading,
  MacroAvatar,
  MacroText,
  MacroActions,
  MacroActionsButtonsGroup,
  MacroActionsCopiedText,
} from './DTMLTroubleshootingAgentAssistNode.styles';
import { ButtonPrimary, ButtonSecondaryOutlinedGray } from 'components/common/buttonsComponents';
import { IconBotFilled } from 'components/common/SVGIcons';

const RESPONSE_EDGE_COMPLETE = 'troubleshooting_complete';
const RESPONSE_EDGE_NOT_HELPFUL = 'not_helpful';

const DTMLTroubleshootingAgentAssistNode = () => {
  const dispatch = useDispatch();
  const lastIncomingMessage = useSelector(extractLastIncomingMessage);
  const selectedInboxTab = useSelector(extractSelectedInboxTab);
  const editorState = useSelector(getNewMessagesEditor);
  const selectedConversationId = useSelector(extractNonHistoricalTicketIdFromState);
  const selectedTicket = useSelector(extractSelectedTicketFromState);
  const getAgentAssist = useAgentAssist();
  const { macroText, isIdle, isLoading, request, trackNotHelpfulClick, trackCopyClick } = getAgentAssist();

  const [copied, setCopied] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const isIdleRef = useRef(isIdle);

  const handleCopyButtonClick = () => {
    if (!shouldCopyMacro(selectedInboxTab)) {
      return;
    }

    try {
      const { ticketType, communicationType } = selectedTicket;
      const macro = messageToClientFormat(macroText, ticketType, communicationType);
      const newEditorState = DraftHelpers.appendHtmlToContents(macro, editorState);
      dispatch(saveNewMessageEditorState(selectedConversationId, newEditorState));
      setCopied(true);
      trackCopyClick();
    } catch (err) {
      trackAgentAssistUsefulnessFailure(err.message);
    }
  };

  const handleNotUsefulClick = () => {
    try {
      dispatch(addEdgeToDecisionTreePath(RESPONSE_EDGE_NOT_HELPFUL));
      trackNotHelpfulClick();
    } catch (err) {
      trackAgentAssistUsefulnessFailure(err.message);
    }
  };

  const handleRegenerateButtonClick = () => {
    request();
    setCopied(false);
    setShowFooter(false);
  };

  useEffect(() => {
    isIdleRef.current = isIdle;
  }, [isIdle]);

  useEffect(() => {
    if (isIdleRef.current) {
      request();
    } else {
      setShowFooter(true);
    }
  }, [lastIncomingMessage?.messageId, request]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Root>
      <MainBody>
        <Header />
        <Section>
          <MacroHeading>
            <MacroAvatar>
              <IconBotFilled width={16} height={16} />
            </MacroAvatar>
            Agent Assistant
          </MacroHeading>
          <MacroText>{macroText}</MacroText>
          <MacroActions>
            <MacroActionsButtonsGroup>
              <ButtonSecondaryOutlinedGray onClick={handleNotUsefulClick}>Not Helpful</ButtonSecondaryOutlinedGray>
              <ButtonSecondaryOutlinedGray onClick={() => dispatch(addEdgeToDecisionTreePath(RESPONSE_EDGE_COMPLETE))}>
                Troubleshooting Complete
              </ButtonSecondaryOutlinedGray>
            </MacroActionsButtonsGroup>
            {!copied && <ButtonPrimary onClick={handleCopyButtonClick}>Copy</ButtonPrimary>}
            <CSSTransition in={copied} timeout={0} unmountOnExit>
              <MacroActionsCopiedText>Response was added to conversation</MacroActionsCopiedText>
            </CSSTransition>
          </MacroActions>
        </Section>
      </MainBody>
      <Footer show={showFooter} onRegenerateClick={handleRegenerateButtonClick} />
    </Root>
  );
};

export default DTMLTroubleshootingAgentAssistNode;
